import { ApplicationConfig, inject, mergeApplicationConfig, NgZone, provideAppInitializer } from '@angular/core';
import { provideRmaInstance } from '@rma/generic/ng/instance';
import { provideDeviceIdentification } from '@rma/generic/ng/util';
import { Environment, IS_PRODUCTION, PlatformService, provideRmaEnvironment } from '@rma/public/generic/util/environment';
import { HotjarIdentificationService, HotjarScriptService } from '@rma/public/generic/util/tracking/hotjar';
import { provideRmaMetaPixelRouteTracking } from '@rma/public/generic/util/tracking/meta-pixel';
import { environment as devEnvironment } from '../environment/environment';
import { commonConfig } from './app.config.common';

declare let environment: Environment;

const env = IS_PRODUCTION ? environment : devEnvironment;

const browserConfig: ApplicationConfig = {
  providers: [
    provideRmaInstance(env.tenantCode, env.type),
    // this overrides the one that is injected from ssr, so we need to make sure it is done only in the browser version
    provideRmaEnvironment(env),
    // This is cookie based, so it can't be loaded on the server
    provideDeviceIdentification(),
    provideRmaMetaPixelRouteTracking(),
    // Temporary: app-wide Hotjar for the concierge "find me an agent" consult test. Inlined rather
    // than using provideRmaHotJar() because that provider is shared with the survey app, which opts
    // into it already — reviving its body would silently start recording survey sessions too.
    // Browser-only config so the SSR render can never emit the script tag.
    provideAppInitializer(() => {
      const platformService = inject(PlatformService);
      const hotjarScriptService = inject(HotjarScriptService);
      const hotjarIdentificationService = inject(HotjarIdentificationService);
      const zone = inject(NgZone);

      // Identification is gated on the id as well as the platform: without one appendScript() bails,
      // window.hj never exists, and identify() would log a console error on every user emission.
      if (platformService.isTrackable && env.tracking.hotjarId) {
        zone.runOutsideAngular(() => {
          hotjarScriptService.appendScript();
          hotjarIdentificationService.initialiseIdentification();
        });
      }
    }),
  ],
};

export const config = mergeApplicationConfig(commonConfig, browserConfig);
